import * as React from 'react';
import {
  HStack,
  View,
  Text,
  SlugIcon,
  Tooltip,
  TextProps,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {ToolsLayerView} from '@packages/systems/ui/shared-ui/ToolsLayerView';
import {getGridFrameLayoutCSS} from '../getGridFrameLayoutCSS';
import {Viewport} from '@packages/systems/open-canvas/core';
import {useMeasuredFrameSize} from '../useMeasuredFrameSize';
import {FrameResizeHandle} from './FrameResizeHandle';
import {
  FrameChromeContextProvider,
  useFrameAccent,
  useFrameSelection,
  type FrameAccent,
} from './FrameChromeContext';
import {PanActivationZone} from '../PanActivationZone';

export type {FrameAccent} from './FrameChromeContext';
export {useFrameAccent} from './FrameChromeContext';

/**
 * The outline that appears around the frame when it is active or selected.
 */
const FrameOutline = emotionStyled(View)(
  {
    pointerEvents: 'none',
    display: 'contents',

    // Draws the outline around the entire frame view
    '&:after': {
      content: '""',
      position: 'absolute',
      inset: '-33px -9px -9px',
      border: '1px solid',
      borderColor: 'transparent',
      pointerEvents: 'none',
      borderRadius: '8px',
    },
  },
  {
    variants: {
      accent: {
        green: {},
        blue: {},
      },
      isActive: {
        true: {
          '&:after': {
            borderColor: '$borderDefault',
          },
        },
      },
      isSelected: {
        true: {},
      },
    },
    compoundVariants: [
      {
        conditions: {
          isSelected: true,
          accent: 'green',
        },
        css: {
          '&:after': {
            borderColor: '$greenCanvas',
          },
        },
      },
      {
        conditions: {
          isSelected: true,
          accent: 'blue',
        },
        css: {
          '&:after': {
            borderColor: '$blueCanvas',
          },
        },
      },
    ],
    defaultVariants: {
      accent: 'blue',
    },
  }
);

const FRAME_VIEW_CONTAINER_NAME = 'frame-view-container';

// Stacking order for adjacent frames in the same grid: the frame under the
// pointer wins, then the active/selected one, then frames at rest. `FrameGrid`
// is the confining stacking context — it sets `isolation: isolate` explicitly
// so these only reorder frames within a single grid and stay deliberately
// local rather than joining the shared scale in `packages/utilities/styled`
// (CANV-2375).
//
// This also depends on `PanActivationZone` — the per-frame wrapper between
// `FrameGrid` and each `FrameView` — staying stacking-transparent. Giving it a
// `position`, `transform`, `opacity`, `filter`, or `isolation` would make every
// `FrameView` the only child of its own stacking context, silently reverting
// this ordering to DOM order with no test failure.
//
// One raised tier covers active and selected together because in practice at
// most one frame carries it: frame selection and element selection are mutually
// exclusive (`maybeClearMCCFrame`), and every path that moves the active
// breakpoint either goes through the element selection that clears the frame
// selection, or moves the frame selection along with it. If two frames ever do
// share the tier, they tie and DOM order decides — today's behavior.
const FRAME_STACK_RAISED = 1;
const FRAME_STACK_HOVERED = 2;

const FrameView = emotionStyled(View)(
  {
    containerName: FRAME_VIEW_CONTAINER_NAME,
    containerType: 'size',
  },
  {
    variants: {
      accent: {
        green: {},
        blue: {},
      },
      // Highlight the tools outline whenever anything within `FrameView` is hovered
      isSelectable: {
        true: {
          // Outranks the raised tier below by design: `:has()` takes the
          // specificity of its argument, so this selector beats the plain-class
          // rules no matter what order the variants are merged in.
          '&:has(:hover)': {
            zIndex: FRAME_STACK_HOVERED,
          },
        },
      },
      isRaised: {
        true: {
          zIndex: FRAME_STACK_RAISED,
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          isSelectable: true,
          accent: 'green',
        },
        css: {
          [`&:has(:hover) ${FrameOutline}:after`]: {
            borderColor: '$greenCanvas',
          },
        },
      },
      {
        conditions: {
          isSelectable: true,
          accent: 'blue',
        },
        css: {
          [`&:has(:hover) ${FrameOutline}:after`]: {
            borderColor: '$blueCanvas',
          },
        },
      },
    ],
    defaultVariants: {
      accent: 'blue',
    },
  }
);

type FrameProps = React.PropsWithChildren<{
  frameId: string;
  getFrameDOMElement: (frameId: string) => HTMLElement | null;
  isSelectable?: boolean;
  /**
   * Either this or `isSelected` raises the frame above adjacent frames at rest,
   * so a neighbour's opaque chrome cannot clip its outline where the two overlap
   * at low zoom. A hovered frame still outranks both (CANV-2375).
   */
  isActive?: boolean;
  /** See `isActive`. */
  isSelected?: boolean;
  /**
   * Accent color applied to the frame's UI. Defaults to `blue`.
   */
  accent?: FrameAccent;
  'data-automation-id'?: string;
}>;

/**
 * The top-level container for the frame and its tools.
 * Use this as your Frame's outermost container with `FrameUI.Frame`.
 * All other UI components need to be children of this container.
 */
const Frame = ({
  frameId,
  getFrameDOMElement,
  isSelectable,
  isActive,
  isSelected,
  accent = 'blue',
  children,
  'data-automation-id': dataAutomationId,
}: FrameProps) => {
  const frameSize = useMeasuredFrameSize({
    frameId,
    getFrameDOMElement,
  });

  const style = React.useMemo(() => {
    return {
      position: 'relative',
      width: `calc(${frameSize.width}px * var(${FrameUI.FRAME_GRID_VIEWPORT_K_CSS_VAR}))`,
      height: `calc(${frameSize.height}px * var(${FrameUI.FRAME_GRID_VIEWPORT_K_CSS_VAR}))`,
    } as React.CSSProperties;
  }, [frameSize]);

  // An inline literal would be a new reference on every render, breaking
  // the smart context's reference-equality check for every descendant.
  const chromeState = React.useMemo(
    () => ({
      accent,
      isActive: Boolean(isActive),
      isSelected: Boolean(isSelected),
    }),
    [accent, isActive, isSelected]
  );

  return (
    <FrameChromeContextProvider value={chromeState}>
      <PanActivationZone>
        <FrameView
          style={style}
          data-automation-id={dataAutomationId}
          isSelectable={isSelectable}
          isRaised={isActive || isSelected}
          accent={accent}
        >
          {children}
        </FrameView>
      </PanActivationZone>
    </FrameChromeContextProvider>
  );
};

type FrameGridProps = React.PropsWithChildren<{
  orientation: 'horizontal' | 'vertical';
  gap: number;
  /**
   * When > 1, mirrors the 2D grid layout produced by the Frame Layout atom
   */
  crossAxisTrackCount?: number;
  viewport: Viewport;
  'data-automation-id'?: string;
}>;

const FrameGrid = ({
  'data-automation-id': dataAutomationId,
  orientation,
  gap,
  crossAxisTrackCount,
  viewport,
  children,
}: FrameGridProps) => {
  const gridStyle = {
    ...getGridFrameLayoutCSS(
      orientation,
      gap * viewport.k,
      crossAxisTrackCount
    ),
    // NOTE: Setting the viewport `k` to a CSS variable is intentional. We avoid passing it as a prop to individual frame views.
    // This is because we only need it to size the individual items in the grid, and otherwise changes to the viewport should NOT
    // trigger a re-render of all frames. This is a performance optimization to prevent re-renders and lag on large amount of frames.
    [FrameUI.FRAME_GRID_VIEWPORT_K_CSS_VAR]: `${viewport.k}`,
    position: 'absolute',
    transform: `translate(${-viewport.x * viewport.k}px, ${-viewport.y * viewport.k}px)`,
    // Confines the frame stacking order in `FrameView.tsx` to this grid,
    // independent of the `transform` above (CANV-2375).
    isolation: 'isolate',
  } as const;

  return (
    <div data-automation-id={dataAutomationId} style={gridStyle}>
      {children}
    </div>
  );
};

type FrameOutlineProps = Omit<
  React.ComponentProps<typeof FrameOutline>,
  'isActive' | 'isSelected'
>;

const Outline = (props: FrameOutlineProps) => {
  const accent = useFrameAccent();
  const {isActive, isSelected} = useFrameSelection();
  return (
    <FrameOutline
      {...props}
      accent={accent}
      isActive={isActive}
      isSelected={isSelected}
    />
  );
};

type FrameHitAreasProps = {
  onClick: () => void;
};

/**
 * Renders additional hit areas to the right, bottom and left of the toolbar.
 * These hit areas are used to select the frame when clicking in the gutters between the Frame and outline.
 */
const FrameHitAreas = (props: FrameHitAreasProps) => {
  return (
    <>
      <RightHitArea
        data-automation-id="toolbar-hit-area-right"
        onClick={props.onClick}
      />
      <BottomHitArea
        data-automation-id="toolbar-hit-area-bottom"
        onClick={props.onClick}
      />
      <LeftHitArea
        data-automation-id="toolbar-hit-area-left"
        onClick={props.onClick}
      />
    </>
  );
};

/**
 * Indicates the pixel offset of the toolbar from the top of the frame
 */
const TOOLBAR_OFFSET_PX = 32;

const HitArea = emotionStyled(ToolsLayerView)({
  position: 'absolute',
  background: '$uiCanvasBackground',
});

const RightHitArea = emotionStyled(HitArea)({
  width: '8px',
  height: `calc(100% + 8px + ${TOOLBAR_OFFSET_PX}px)`,
  right: '-8px',
  top: `-${TOOLBAR_OFFSET_PX}px`,
  borderTopRightRadius: '7px',
  borderBottomRightRadius: '7px',
});

const BottomHitArea = emotionStyled(HitArea)({
  width: '100%',
  height: '8px',
  bottom: '-8px',
});

const LeftHitArea = emotionStyled(HitArea)({
  width: '8px',
  height: `calc(100% + 8px + ${TOOLBAR_OFFSET_PX}px)`,
  left: '-8px',
  top: `-${TOOLBAR_OFFSET_PX}px`,
  borderTopLeftRadius: '7px',
  borderBottomLeftRadius: '7px',
});

/**
 * The wrapper for the toolbar area that appears above the frame.
 */
const FrameToolbar = emotionStyled(ToolsLayerView)({
  position: 'absolute',
  width: '100%',
  top: `${-TOOLBAR_OFFSET_PX}px`,
  backgroundColor: '$uiCanvasBackground',
  height: '$8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$1',
});

/**
 * The left section of the toolbar area, which contains the frame label and other left-aligned controls.
 */
const FrameToolbarLeft = emotionStyled(View)({
  display: 'flex',
  alignItems: 'center',
  gap: '$1',
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
});

/**
 * The right section of the toolbar area, which contains right-aligned controls.
 */
const FrameToolbarRight = emotionStyled(View)({
  display: 'flex',
  alignItems: 'center',
  gap: '$1',
  flexShrink: 0,
});

const LabelContainer = emotionStyled(HStack)(
  {
    flex: '1 1 auto',
    backgroundColor: 'transparent',
    borderRadius: '$2',
    minWidth: 0,
    width: 'auto',
    textWrap: 'nowrap',
    color: '$textSecondary',
  },
  {
    variants: {
      accent: {
        green: {},
        blue: {},
      },
      selected: {
        true: {},
      },
    },
    compoundVariants: [
      {
        conditions: {
          selected: true,
          accent: 'green',
        },
        css: {
          color: '$greenText',
        },
      },
      {
        conditions: {
          selected: true,
          accent: 'blue',
        },
        css: {
          color: '$blueText',
        },
      },
    ],
    defaultVariants: {
      accent: 'blue',
    },
  }
);

const IconWrapper = emotionStyled(View)(
  {
    color: '$textSecondary',
  },
  {
    variants: {
      accent: {
        green: {},
        blue: {},
      },
      selected: {
        true: {},
      },
    },
    compoundVariants: [
      {
        conditions: {
          selected: true,
          accent: 'green',
        },
        css: {
          color: '$greenText',
        },
      },
      {
        conditions: {
          selected: true,
          accent: 'blue',
        },
        css: {
          color: '$blueText',
        },
      },
    ],
    defaultVariants: {
      accent: 'blue',
    },
  }
);

const FrameIcon = ({
  isSelected,
  children,
  ...rest
}: {
  isSelected: boolean;
  children: React.ReactNode;
}) => {
  const accent = useFrameAccent();
  return (
    <IconWrapper accent={accent} selected={isSelected} {...rest}>
      {children}
    </IconWrapper>
  );
};

type FrameLabelProps = {
  label: string;
  isSelected?: boolean;
  onDoubleClick?: () => void;
  ['data-automation-id']?: string;
} & TextProps;

const truncationTooltipProps = {
  _shouldNotBeTabbable: true,
};

const FrameLabel = ({
  label,
  isSelected,
  onDoubleClick,
  'data-automation-id': dataAutomationId,
  ...rest
}: FrameLabelProps) => {
  const accent = useFrameAccent();

  return (
    <LabelContainer
      data-automation-id={dataAutomationId}
      spacing={1}
      selected={isSelected}
      accent={accent}
      onDoubleClick={onDoubleClick}
    >
      <Text
        truncateToLines={1}
        truncationTooltipProps={truncationTooltipProps}
        weight={isSelected ? 'semibold' : 'normal'}
        {...rest}
      >
        {label}
      </Text>
    </LabelContainer>
  );
};

const BaseIndicatorContainer = emotionStyled(ToolsLayerView)(
  {
    position: 'absolute',
    height: '$8',
    left: '$-4',
    top: `${-TOOLBAR_OFFSET_PX}px`,
    display: 'flex',
    alignItems: 'center',
    color: '$textSecondary',

    '.toolbar-slug-icon': {
      backgroundColor: '$uiCanvasBackground',
    },
  },
  {
    variants: {
      accent: {
        green: {},
        blue: {},
      },
      selected: {
        true: {},
      },
    },
    compoundVariants: [
      {
        conditions: {
          selected: true,
          accent: 'green',
        },
        css: {
          color: '$greenText',
        },
      },
      {
        conditions: {
          selected: true,
          accent: 'blue',
        },
        css: {
          color: '$blueText',
        },
      },
    ],
    defaultVariants: {
      accent: 'blue',
    },
  }
);

const DEFAULT_BASE_INDICATOR_TOOLTIP_MAX_WIDTH = 146;

type FrameBaseIndicatorProps = {
  isSelected: boolean;
  tooltipTitle: string;
  tooltipLabel: string;
  tooltipMaxWidth?: number;
  tabIndex?: number;
};

const FrameBaseIndicator = ({
  isSelected,
  tooltipTitle,
  tooltipLabel,
  tooltipMaxWidth = DEFAULT_BASE_INDICATOR_TOOLTIP_MAX_WIDTH,
  tabIndex,
  ...rest
}: FrameBaseIndicatorProps) => {
  const accent = useFrameAccent();
  return (
    <BaseIndicatorContainer
      selected={isSelected}
      accent={accent}
      tabIndex={tabIndex}
      {...rest}
    >
      <Tooltip
        title={tooltipTitle}
        label={tooltipLabel}
        maxWidth={tooltipMaxWidth}
        _shouldNotBeTabbable={tabIndex === -1}
      >
        <SlugIcon className="toolbar-slug-icon" />
      </Tooltip>
    </BaseIndicatorContainer>
  );
};
/**
 * Export all building blocks under FrameUI
 */
export const FrameUI = {
  FrameGrid,
  Frame,
  Outline,
  HitAreas: FrameHitAreas,
  ResizeHandle: FrameResizeHandle,
  Toolbar: FrameToolbar,
  ToolbarLeft: FrameToolbarLeft,
  ToolbarRight: FrameToolbarRight,
  Label: FrameLabel,
  Icon: FrameIcon,
  BaseIndicator: FrameBaseIndicator,

  /**
   * The name of the container that is used for CSS container queries.
   */
  CONTAINER_NAME: FRAME_VIEW_CONTAINER_NAME,

  /**
   * The CSS variable name that contains the viewport `k` value.
   */
  FRAME_GRID_VIEWPORT_K_CSS_VAR: '--var-frame-grid-viewport-k',
};
